"use client";

import { useCallback, useEffect, useRef, useState } from "react";

type WebkitWindow = Window & typeof globalThis & {
  webkitAudioContext?: typeof AudioContext;
};

export type LegacyAudioCue = "land" | "clear" | "message" | "login";

export interface LegacyAudioAsset {
  readonly durationSeconds: number;
  readonly path: string;
  readonly trigger: string;
}

/**
 * The battle sounds and the original room-message cue extracted from the
 * user-provided SWF; keeping the metadata here makes the audio contract
 * testable.
 */
export const LEGACY_AUDIO_ASSETS = {
  land: {
    durationSeconds: 0.9142,
    path: "/audio/bwwk-land.mp3",
    trigger: "lock-without-line-clear",
  },
  clear: {
    durationSeconds: 1.2278,
    path: "/audio/bwwk-clear.mp3",
    trigger: "lock-with-one-or-more-lines-cleared",
  },
  message: {
    durationSeconds: 0.2151,
    path: "/audio/bwwk-message.wav",
    trigger: "room-chat-message-submit",
  },
  login: {
    durationSeconds: 0.842,
    path: "/audio/bwwk-login.wav",
    trigger: "room-member-joined",
  },
} as const satisfies Record<LegacyAudioCue, LegacyAudioAsset>;

const LEGACY_AUDIO_CUES = Object.keys(LEGACY_AUDIO_ASSETS) as LegacyAudioCue[];

export type LegacyAudioBuffers = Partial<Record<LegacyAudioCue, AudioBuffer>>;

export type LegacyAudioSettings = Readonly<{
  muted: boolean;
  volume: number;
}>;

export const DEFAULT_LEGACY_AUDIO_SETTINGS: LegacyAudioSettings = {
  muted: false,
  volume: 1,
};

const LEGACY_AUDIO_SETTINGS_KEY = "bwwk-audio-settings";
const LEGACY_AUDIO_SETTINGS_EVENT = "bwwk-audio-settings-change";

function clampVolume(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return DEFAULT_LEGACY_AUDIO_SETTINGS.volume;
  return Math.min(1, Math.max(0, value));
}

export function getLegacyAudioSettings(): LegacyAudioSettings {
  if (typeof window === "undefined") return DEFAULT_LEGACY_AUDIO_SETTINGS;

  try {
    const raw = window.localStorage.getItem(LEGACY_AUDIO_SETTINGS_KEY);
    if (!raw) return DEFAULT_LEGACY_AUDIO_SETTINGS;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return DEFAULT_LEGACY_AUDIO_SETTINGS;
    const candidate = parsed as { muted?: unknown; volume?: unknown };
    return {
      muted: candidate.muted === true,
      volume: clampVolume(candidate.volume),
    };
  } catch {
    return DEFAULT_LEGACY_AUDIO_SETTINGS;
  }
}

export function saveLegacyAudioSettings(next: LegacyAudioSettings): LegacyAudioSettings {
  const settings: LegacyAudioSettings = {
    muted: next.muted === true,
    volume: clampVolume(next.volume),
  };

  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(LEGACY_AUDIO_SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      // Private browsing can reject localStorage; audio still works for this session.
    }
    window.dispatchEvent(new Event(LEGACY_AUDIO_SETTINGS_EVENT));
  }

  return settings;
}

export function useLegacyAudioSettings() {
  const [settings, setSettings] = useState<LegacyAudioSettings>(getLegacyAudioSettings);

  useEffect(() => {
    const refresh = () => setSettings(getLegacyAudioSettings());
    window.addEventListener(LEGACY_AUDIO_SETTINGS_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(LEGACY_AUDIO_SETTINGS_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const updateSettings = useCallback((next: LegacyAudioSettings) => {
    setSettings(saveLegacyAudioSettings(next));
  }, []);

  return { settings, updateSettings };
}

export type LegacyAudioFetcher = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

export interface LegacyAudioDecoder {
  decodeAudioData(audioData: ArrayBuffer): Promise<AudioBuffer>;
}

export function legacyAudioCueForLock(clearedLines: number): LegacyAudioCue {
  return clearedLines > 0 ? "clear" : "land";
}

/**
 * Decode both samples once. Each file is isolated so one unavailable sample
 * never delays or disables the other, and callers can safely retry preload.
 */
export async function loadLegacyAudioBuffers(
  decoder: LegacyAudioDecoder,
  fetchAudio: LegacyAudioFetcher = fetch,
): Promise<LegacyAudioBuffers> {
  const decoded = await Promise.all(
    LEGACY_AUDIO_CUES.map(async (cue) => {
      try {
        const response = await fetchAudio(LEGACY_AUDIO_ASSETS[cue].path, {
          cache: "force-cache",
        });
        if (!response.ok) return [cue, undefined] as const;
        const bytes = await response.arrayBuffer();
        const buffer = await decoder.decodeAudioData(bytes.slice(0));
        return [cue, buffer] as const;
      } catch {
        return [cue, undefined] as const;
      }
    }),
  );

  const buffers: LegacyAudioBuffers = {};
  for (const [cue, buffer] of decoded) {
    if (buffer) buffers[cue] = buffer;
  }
  return buffers;
}

export function useLegacyAudio() {
  const contextRef = useRef<AudioContext | null>(null);
  const buffersRef = useRef<LegacyAudioBuffers>({});
  const preloadRef = useRef<Promise<void> | null>(null);
  const outputGainRef = useRef<GainNode | null>(null);
  const pendingCuesRef = useRef<LegacyAudioCue[]>([]);

  const getContext = useCallback(() => {
    if (contextRef.current) return contextRef.current;
    const AudioContextClass = window.AudioContext ?? (window as WebkitWindow).webkitAudioContext;
    if (!AudioContextClass) return null;
    contextRef.current = new AudioContextClass();
    return contextRef.current;
  }, []);

  const getOutputGain = useCallback((context: AudioContext) => {
    if (outputGainRef.current) return outputGainRef.current;

    const settings = getLegacyAudioSettings();
    const gain = context.createGain();
    gain.gain.value = settings.muted ? 0 : settings.volume;
    gain.connect(context.destination);
    outputGainRef.current = gain;
    return gain;
  }, []);

  useEffect(() => {
    const syncOutputGain = () => {
      const context = contextRef.current;
      const gain = outputGainRef.current;
      if (!context || !gain || context.state === "closed") return;

      const settings = getLegacyAudioSettings();
      gain.gain.setTargetAtTime(settings.muted ? 0 : settings.volume, context.currentTime, 0.01);
    };

    window.addEventListener(LEGACY_AUDIO_SETTINGS_EVENT, syncOutputGain);
    window.addEventListener("storage", syncOutputGain);
    return () => {
      window.removeEventListener(LEGACY_AUDIO_SETTINGS_EVENT, syncOutputGain);
      window.removeEventListener("storage", syncOutputGain);
    };
  }, []);

  const preload = useCallback(async (context: AudioContext) => {
    if (LEGACY_AUDIO_CUES.every((cue) => buffersRef.current[cue])) return;
    if (!preloadRef.current) {
      preloadRef.current = loadLegacyAudioBuffers(context)
        .then((buffers) => {
          Object.assign(buffersRef.current, buffers);
        })
        .finally(() => {
          preloadRef.current = null;
        });
    }
    await preloadRef.current;
  }, []);

  const playDecodedCue = useCallback((cue: LegacyAudioCue, context: AudioContext): boolean => {
    const buffer = buffersRef.current[cue];
    if (!buffer || context.state !== "running") return false;

    // A fresh source for every trigger preserves the original overlapping
    // attachSound/start behavior when both boards settle close together.
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(getOutputGain(context));
    source.start(context.currentTime);
    return true;
  }, [getOutputGain]);

  const flushPendingCues = useCallback((context: AudioContext) => {
    const settings = getLegacyAudioSettings();
    if (settings.muted || settings.volume <= 0) {
      pendingCuesRef.current = [];
      return;
    }
    if (context.state !== "running") return;

    const pending = pendingCuesRef.current.splice(0);
    for (const cue of pending) {
      if (!playDecodedCue(cue, context)) pendingCuesRef.current.push(cue);
    }
  }, [playDecodedCue]);

  const unlock = useCallback(async () => {
    const context = getContext();
    if (!context) return;
    try {
      if (context.state === "suspended") await context.resume();
      if (context.state !== "closed") {
        await preload(context);
        flushPendingCues(context);
      }
    } catch {
      // Browsers can reject resume() until a user gesture arrives. The next
      // keyboard/button gesture will retry and flush any queued cue.
    }
  }, [flushPendingCues, getContext, preload]);

  const playCue = useCallback(
    (cue: LegacyAudioCue) => {
      const settings = getLegacyAudioSettings();
      if (settings.muted || settings.volume <= 0) return;

      const context = getContext();
      if (!context || context.state === "closed") return;

      if (!playDecodedCue(cue, context)) {
        // The first lock can race the async decode or the browser's audio
        // resume. Do not silently lose it; play it after the next successful
        // user-gesture unlock.
        pendingCuesRef.current.push(cue);
        void preload(context).then(() => flushPendingCues(context)).catch(() => undefined);
        return;
      }
    },
    [flushPendingCues, getContext, playDecodedCue, preload],
  );

  const playLock = useCallback(
    (clearedLines: number) => {
      playCue(legacyAudioCueForLock(clearedLines));
    },
    [playCue],
  );

  useEffect(
    () => () => {
      buffersRef.current = {};
      preloadRef.current = null;
      outputGainRef.current = null;
      pendingCuesRef.current = [];
      const context = contextRef.current;
      contextRef.current = null;
      if (context && context.state !== "closed") void context.close();
    },
    [],
  );

  return { playCue, playLock, unlock };
}
